import type { Locale } from '@sb2/shared';

export type MessageKey = keyof typeof en;

const en = {
  brandTagline: 'Every answer matters.',
  footerNote: 'Football quiz · race the clock',
  navSolo: 'Solo',
  navMultiplayer: 'Multiplayer',
  navProfile: 'Profile',
  navLeaderboard: 'Leaderboard',
  navAdmin: 'Admin',
  guest: 'guest',
  admin: 'admin',
  signedOut: 'signed out',
  nameGateTitle: 'Pick a name',
  nameGateHint: 'Choose a display name so you show up on the leaderboard.',
  nameGateRules: '3–16 characters. Letters, numbers, and underscores. Starts with a letter.',
  nameSaveFail: 'Could not save that name',
  profilePlayingAs: 'Playing as {name}',
  profileChangeName: 'Change name',
  profileNameSaved: 'Name saved — you’ll appear on high scores.',

  homeEyebrow: 'Football quiz',
  homeLede: 'Five seconds a question. The board is already against you.',
  homeBody:
    'Race the clock on every question. Solo timed, lives, or time bank — and multiplayer races for up to five players.',
  homePlaySolo: 'Play solo',
  homeMultiplayer: 'Multiplayer',
  homeDaily: 'Daily challenge',

  soloTitle: 'Solo quiz',
  soloIntro: 'Pick a mode. Difficulty rises with your streak.',
  soloTimed: 'Timed',
  soloTimedDesc: 'Five seconds per answer — race the clock',
  soloLives: 'Longest streak',
  soloLivesDesc: 'Three lives — survive as long as you can',
  soloTimeBank: 'Time bank',
  soloTimeBankDesc: 'One shared clock — bank time with correct answers',
  soloDaily: 'Daily challenge',
  soloDailyDesc: 'Ten shared questions — one run, one day board',
  soloDailyRules: 'Same ten questions for everyone today (UTC). One attempt.',
  soloDailyPlayed: 'Already played today — see the daily board.',
  soloDailyProgress: 'Question {n} of {total}',
  soloOnboardingHint: 'First run is a shorter, easier warm-up.',
  soloTimeBankRules: 'Start with 60s. Correct +2s, wrong −3s. Ends at zero.',
  soloReadingRules: 'Each question shows for 1–2 seconds, then you have 5 seconds to answer.',
  soloRead: 'Read',
  soloStart: 'Start',
  soloMin: 'min',
  soloSec: 's',
  soloEnded: 'Run over',
  soloBestStreak: 'Best streak',
  soloPlayAgain: 'Play again',
  soloScore: 'Score',
  soloStreak: 'Streak',
  soloLivesLabel: 'Lives',
  soloMatch: 'Match',
  soloBank: 'Bank',
  soloCorrect: 'Correct',
  soloWrong: 'Wrong',
  soloTimeouts: 'Timeouts',
  soloShare: 'Share result',
  soloViewBoard: 'Leaderboard',

  lobbyTitle: 'Multiplayer',
  lobbyIntro: 'Timed races only — 1v1 or groups up to {max}.',
  lobbyPlayers: 'Players',
  lobbyQuick: 'Quick match',
  lobbyCreatePublic: 'Create public room',
  lobbyPublicRooms: 'Open public rooms',
  lobbyNoPublic: 'No public rooms waiting right now.',
  lobbyRefresh: 'Refresh',
  lobbyPublic: 'Public',
  lobbyPrivate: 'Private',
  lobbyPrivateHint: 'Share the code so friends can join. Leave blank to generate one.',
  lobbyShareCode: 'Room code',
  lobbyRoomCode: 'Room code',
  lobbyCreate: 'Create private',
  lobbyJoin: 'Join',
  lobbyWaiting: 'Waiting for players — press ready when set.',
  lobbyReady: 'Ready',
  lobbyMatchLeft: 'Match time left {sec}s',
  lobbyNext: 'Next question…',
  lobbyFinished: 'Finished',
  lobbyWinner: 'Winner',
  lobbyWinners: 'Winners',
  lobbyRematch: 'Rematch',
  lobbyRematchVotes: 'Rematch {votes}/{needed}',
  lobbyLeave: 'Leave',
  lobbyAway: 'away',
  lobbyEnterCode: 'Enter a room code',
  lobbyConnectFail: 'Could not connect to the room',
  lobbyConnecting: 'Connecting…',
  lobbyCopiedCode: 'Code copied',

  profileTitle: 'Profile',
  profileUsername: 'Username',
  profileSave: 'Save',
  profileLinkGoogle: 'Link Google',
  profileRecent: 'Recent runs',
  profileNoRuns: 'No runs yet.',
  profileRating: 'Rating',
  profilePeak: 'peak',
  profileQuizGames: 'Quiz games',
  profileBestTimed: 'Best timed score',
  profileBestStreak: 'Best streak',
  profileAnswers: 'Answers',
  profileCorrect: 'correct',
  profileWrong: 'wrong',
  profileRanked: 'Ranked W-L-D',

  lbTitle: 'Leaderboard',
  lbRating: 'Rating',
  lbScore: 'Score',
  lbStreak: 'Streak',
  lbWins: 'Wins',
  lbWeekly: 'This week',
  lbPlayer: 'Player',
  lbWhen: 'When',
  lbPlays: 'Plays',
  lbCareer: 'Career',
  lbGameType: 'Game type',
  lbDuration: 'Duration',
  lbAnyDuration: 'Any length',
  lbAllTime: 'All time',
  lbMultiplayer: 'Multiplayer',
  lbDaily: 'Daily',
  lbToday: 'Today',
  lbEmpty: 'No plays for this filter yet.',
  lbRunsHint: 'Top 100 plays for one game type — the same player can appear more than once.',
  lbUsersHint: 'One row per player (career totals).',

  settingsLanguage: 'Language',
  settingsAdminUnlock: 'Unlock admin',
  settingsAdminEnabled: 'Admin enabled',

  adminTitle: 'Question editor',
  adminAccess: 'Admin access required.',
  adminMatching: '{total} matching · inspect drafts before approving',
  adminRefresh: 'Refresh',
  adminPrompt: 'Prompt',
  adminChoice: 'Choice',
  adminCorrect: 'Correct index',
  adminDifficulty: 'Difficulty',
  adminStats: 'Stats',
  adminCorrectShort: 'correct',
  adminWrongShort: 'wrong',
  adminTimeouts: 'timeouts',
  adminSave: 'Save',
  adminApprove: 'Approve',
  adminReject: 'Reject',
  adminSelect: 'Select a question',
  adminAll: 'all',
  adminHottest: 'Hottest',
  adminNeverServed: 'Never served',
  adminStems: 'Stem spam',
  adminRejectStem: 'Reject stem excess',
  adminServed: 'served',
  diffEasy: 'easy',
  diffMedium: 'medium',
  diffHard: 'hard',

  loading: 'Loading…',
} as const;

const hr: Record<MessageKey, string> = {
  brandTagline: 'Svaki odgovor broji.',
  footerNote: 'Nogometni kviz · trka s vremenom',
  navSolo: 'Solo',
  navMultiplayer: 'Za više igrača',
  navProfile: 'Profil',
  navLeaderboard: 'Ljestvica',
  navAdmin: 'Admin',
  guest: 'gost',
  admin: 'admin',
  signedOut: 'odjavljen',
  nameGateTitle: 'Odaberi ime',
  nameGateHint: 'Odaberi nadimak da se pojaviš na ljestvici.',
  nameGateRules: '3–16 znakova. Slova, brojevi i donje crte. Počinje slovom.',
  nameSaveFail: 'Nije moguće spremiti to ime',
  profilePlayingAs: 'Igraš kao {name}',
  profileChangeName: 'Promijeni ime',
  profileNameSaved: 'Ime spremljeno — bit ćeš na ljestvici.',

  homeEyebrow: 'Nogometni kviz',
  homeLede: 'Pet sekundi po pitanju. Tabla je već protiv tebe.',
  homeBody:
    'Trka s vremenom na svakom pitanju. Solo na vrijeme, sa životima ili time bank — te utrke za više igrača do petero.',
  homePlaySolo: 'Igraj solo',
  homeMultiplayer: 'Za više igrača',
  homeDaily: 'Dnevni izazov',

  soloTitle: 'Solo kviz',
  soloIntro: 'Odaberi način. Težina raste s nizom točnih.',
  soloTimed: 'Na vrijeme',
  soloTimedDesc: 'Pet sekundi po odgovoru — trka s vremenom',
  soloLives: 'Najduži niz',
  soloLivesDesc: 'Tri života — izdrži što dulje',
  soloTimeBank: 'Banka vremena',
  soloTimeBankDesc: 'Jedan zajednički sat — zarađuj vrijeme točnim odgovorima',
  soloDaily: 'Dnevni izazov',
  soloDailyDesc: 'Deset zajedničkih pitanja — jedna igra, dnevna ljestvica',
  soloDailyRules: 'Ista deset pitanja za sve danas (UTC). Jedan pokušaj.',
  soloDailyPlayed: 'Već odigrano danas — pogledaj dnevnu ljestvicu.',
  soloDailyProgress: 'Pitanje {n} od {total}',
  soloOnboardingHint: 'Prva igra je kraći, lakši uvod.',
  soloTimeBankRules: 'Počni s 60s. Točno +2s, krivo −3s. Kraj na nuli.',
  soloReadingRules: 'Svako pitanje se prikaže 1–2 sekunde, zatim imaš 5 sekundi za odgovor.',
  soloRead: 'Čitaj',
  soloStart: 'Kreni',
  soloMin: 'min',
  soloSec: 's',
  soloEnded: 'Kraj',
  soloBestStreak: 'Najbolji niz',
  soloPlayAgain: 'Igraj opet',
  soloScore: 'Bodovi',
  soloStreak: 'Niz',
  soloLivesLabel: 'Životi',
  soloMatch: 'Utakmica',
  soloBank: 'Banka',
  soloCorrect: 'Točno',
  soloWrong: 'Krivo',
  soloTimeouts: 'Isteklo',
  soloShare: 'Podijeli rezultat',
  soloViewBoard: 'Ljestvica',

  lobbyTitle: 'Za više igrača',
  lobbyIntro: 'Samo utrke na vrijeme — 1v1 ili grupe do {max}.',
  lobbyPlayers: 'Igrači',
  lobbyQuick: 'Brzi meč',
  lobbyCreatePublic: 'Stvori javnu sobu',
  lobbyPublicRooms: 'Otvorene javne sobe',
  lobbyNoPublic: 'Trenutno nema javnih soba koje čekaju.',
  lobbyRefresh: 'Osvježi',
  lobbyPublic: 'Javna',
  lobbyPrivate: 'Privatna',
  lobbyPrivateHint: 'Podijeli kod s prijateljima. Ostavi prazno za automatski kod.',
  lobbyShareCode: 'Kod sobe',
  lobbyRoomCode: 'Kod sobe',
  lobbyCreate: 'Stvori privatnu',
  lobbyJoin: 'Pridruži se',
  lobbyWaiting: 'Čekamo igrače — stisni Spreman kad si spreman.',
  lobbyReady: 'Spreman',
  lobbyMatchLeft: 'Preostalo vremena {sec}s',
  lobbyNext: 'Sljedeće pitanje…',
  lobbyFinished: 'Gotovo',
  lobbyWinner: 'Pobjednik',
  lobbyWinners: 'Pobjednici',
  lobbyRematch: 'Revanš',
  lobbyRematchVotes: 'Revanš {votes}/{needed}',
  lobbyLeave: 'Izađi',
  lobbyAway: 'odsutan',
  lobbyEnterCode: 'Unesi kod sobe',
  lobbyConnectFail: 'Nije moguće spojiti se na sobu',
  lobbyConnecting: 'Spajanje…',
  lobbyCopiedCode: 'Kod kopiran',

  profileTitle: 'Profil',
  profileUsername: 'Korisničko ime',
  profileSave: 'Spremi',
  profileLinkGoogle: 'Poveži Google',
  profileRecent: 'Nedavne igre',
  profileNoRuns: 'Još nema igara.',
  profileRating: 'Rejting',
  profilePeak: 'vrhunac',
  profileQuizGames: 'Kviz partije',
  profileBestTimed: 'Najbolji rezultat (vrijeme)',
  profileBestStreak: 'Najbolji niz',
  profileAnswers: 'Odgovori',
  profileCorrect: 'točno',
  profileWrong: 'krivo',
  profileRanked: 'Rangirano P-I-N',

  lbTitle: 'Ljestvica',
  lbRating: 'Rejting',
  lbScore: 'Rezultat',
  lbStreak: 'Niz',
  lbWins: 'Pobjede',
  lbWeekly: 'Ovaj tjedan',
  lbPlayer: 'Igrač',
  lbWhen: 'Kada',
  lbPlays: 'Partije',
  lbCareer: 'Karijera',
  lbGameType: 'Vrsta igre',
  lbDuration: 'Trajanje',
  lbAnyDuration: 'Bilo koje',
  lbAllTime: 'Svih vremena',
  lbMultiplayer: 'Multiplayer',
  lbDaily: 'Dnevni',
  lbToday: 'Danas',
  lbEmpty: 'Nema partija za ovaj filter.',
  lbRunsHint: 'Top 100 partija za jedan tip igre — isti igrač može se pojaviti više puta.',
  lbUsersHint: 'Jedan red po igraču (ukupno u karijeri).',

  settingsLanguage: 'Jezik',
  settingsAdminUnlock: 'Otključaj admina',
  settingsAdminEnabled: 'Admin omogućen',

  adminTitle: 'Urednik pitanja',
  adminAccess: 'Potreban je admin pristup.',
  adminMatching: '{total} rezultata · pregledaj nacrte prije odobrenja',
  adminRefresh: 'Osvježi',
  adminPrompt: 'Pitanje',
  adminChoice: 'Odgovor',
  adminCorrect: 'Točan indeks',
  adminDifficulty: 'Težina',
  adminStats: 'Statistika',
  adminCorrectShort: 'točno',
  adminWrongShort: 'krivo',
  adminTimeouts: 'isteklo',
  adminSave: 'Spremi',
  adminApprove: 'Odobri',
  adminReject: 'Odbaci',
  adminSelect: 'Odaberi pitanje',
  adminAll: 'sve',
  adminHottest: 'Najčešća',
  adminNeverServed: 'Nikad servirana',
  adminStems: 'Šablon spam',
  adminRejectStem: 'Odbaci višak šablona',
  adminServed: 'servirano',
  diffEasy: 'lako',
  diffMedium: 'srednje',
  diffHard: 'teško',

  loading: 'Učitavanje…',
};

export const messages: Record<Locale, Record<MessageKey, string>> = { en, hr };

export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  let s = messages[locale][key] ?? messages.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}
